import {
  Activity,
  BarChart3,
  BookMarked,
  BookOpen,
  Boxes,
  Building2,
  CalendarRange,
  Database,
  FileText,
  FolderKanban,
  GitBranch,
  Home,
  LayoutGrid,
  Layers,
  ListTodo,
  Package,
  Settings2,
  Target,
  Ticket,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Navegação principal: itens organizados em quatro grupos funcionais.
 *
 * A lista fica fora do `AppShell` porque o roteador também precisa dela: cada
 * item tem que ter destino, e um item sem rota registrada é erro 404 na cara
 * do usuário. Manter os dois lados lendo do mesmo lugar torna isso difícil de
 * esquecer.
 *
 * Os grupos refletem o que a pessoa faz, não como o sistema é construído:
 * - Operação: o dia a dia de quem atende — chamados, mudanças, base de conhecimento.
 * - Clientes: para quem e com que compromisso se trabalha.
 * - Processo: como o serviço é desenhado e melhorado.
 * - Sistema: infra, dados e configuração global.
 */

export type NavPath =
  | '/'
  | '/chamados'
  | '/mudancas'
  | '/entregas'
  | '/erros-conhecidos'
  | '/conhecimento'
  | '/clientes'
  | '/contratos'
  | '/contratos/dashboard'
  | '/projetos'
  | '/minhas-atividades'
  | '/catalogo'
  | '/fluxos'
  | '/carga-trabalho'
  | '/portfolio'
  | '/relatorio-alocacao'
  | '/sprints'
  | '/okrs'
  | '/melhorias'
  | '/cmdb'
  | '/relatorios'
  | '/administracao'
  | '/diagnostico';

export interface NavItem {
  to: NavPath;
  label: string;
  icon: LucideIcon;
  /** Texto do tooltip quando a lateral está recolhida e só há o ícone. */
  hint: string;
}

export interface NavGroup {
  id: string;
  label: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'operacao',
    label: 'Operação',
    items: [
      { to: '/', label: 'Início', icon: Home, hint: 'Painel inicial' },
      {
        to: '/chamados',
        label: 'Chamados',
        icon: Ticket,
        hint: 'Incidentes, requisições e tarefas',
      },
      {
        to: '/mudancas',
        label: 'Mudanças',
        icon: CalendarRange,
        hint: 'Calendário, janelas e congelamentos',
      },
      {
        to: '/entregas',
        label: 'Entregas',
        icon: Package,
        hint: 'Versões de entrega e o que cada uma agrupa',
      },
      {
        to: '/erros-conhecidos',
        label: 'Erros conhecidos',
        icon: BookMarked,
        hint: 'Base de erros conhecidos e soluções de contorno',
      },
      {
        to: '/conhecimento',
        label: 'Conhecimento',
        icon: BookOpen,
        hint: 'Artigos, procedimentos e o que já foi resolvido antes',
      },
    ],
  },
  {
    id: 'clientes',
    label: 'Clientes',
    items: [
      {
        to: '/clientes',
        label: 'Clientes',
        icon: Building2,
        hint: 'Carteira atendida, filiais e contatos',
      },
      {
        to: '/contratos',
        label: 'Contratos',
        icon: FileText,
        hint: 'Vigências, franquias, cobertura e vencimentos',
      },
      {
        to: '/contratos/dashboard',
        label: 'Dashboard Contratos',
        icon: BarChart3,
        hint: 'Visão geral de contratos — MRR, situações e vencimentos',
      },
    ],
  },
  {
    id: 'processo',
    label: 'Processo',
    items: [
      {
        to: '/projetos',
        label: 'Projetos',
        icon: FolderKanban,
        hint: 'Gestão de projetos, WBS e cronogramas',
      },
      {
        to: '/minhas-atividades',
        label: 'Minhas atividades',
        icon: ListTodo,
        hint: 'Tarefas de projeto atribuídas a mim',
      },
      {
        to: '/carga-trabalho',
        label: 'Carga de trabalho',
        icon: Users,
        hint: 'Distribuição de tarefas de projeto por pessoa',
      },
      {
        to: '/portfolio',
        label: 'Portfólio',
        icon: LayoutGrid,
        hint: 'Linha do tempo de todos os projetos ativos',
      },
      {
        to: '/relatorio-alocacao',
        label: 'Relatório de alocação',
        icon: BarChart3,
        hint: 'Carga de trabalho por membro nos projetos ativos',
      },
      {
        to: '/sprints',
        label: 'Sprints',
        icon: Layers,
        hint: 'Sprints de projetos — planejamento e acompanhamento',
      },
      {
        to: '/okrs',
        label: 'OKRs',
        icon: Target,
        hint: 'Objetivos e resultados-chave estratégicos',
      },
      {
        to: '/catalogo',
        label: 'Catálogo de serviços',
        icon: Boxes,
        hint: 'Serviços de negócio e seus componentes',
      },
      {
        to: '/fluxos',
        label: 'Fluxos',
        icon: GitBranch,
        hint: 'Grafos de estado, rascunhos e publicação',
      },
      {
        to: '/melhorias',
        label: 'Melhorias (CSI)',
        icon: TrendingUp,
        hint: 'Registro de oportunidades de melhoria contínua — ITIL',
      },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      {
        to: '/cmdb',
        label: 'CMDB',
        icon: Database,
        hint: 'Inventário automatizado de ICs — ESXi e Wazuh',
      },
      { to: '/relatorios', label: 'Relatórios', icon: BarChart3, hint: 'Indicadores e exportações' },
      {
        to: '/administracao',
        label: 'Administração',
        icon: Settings2,
        hint: 'Pessoas, equipes, SLA e catálogos',
      },
      { to: '/diagnostico', label: 'Diagnóstico', icon: Activity, hint: 'Autoteste do ambiente' },
    ],
  },
];

export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/**
 * Qual item está ativo para um caminho.
 *
 * `/` casa exato; os demais casam por prefixo de segmento, para que
 * `/chamados/I2603-0001` continue marcando "Chamados". Comparar por
 * `startsWith` puro faria `/chamados-antigos` acender o mesmo item.
 */
export function itemAtivo(pathname: string): NavPath | null {
  if (pathname === '/') return '/';
  // Exact match takes priority — prevents `/contratos/dashboard` from
  // lighting up `/contratos` via the prefix check below.
  for (const item of NAV_ITEMS) {
    if (pathname === item.to) return item.to;
  }
  // Prefix match fallback for detail routes like `/contratos/$contratoId`.
  for (const item of NAV_ITEMS) {
    if (item.to === '/') continue;
    if (pathname.startsWith(`${item.to}/`)) return item.to;
  }
  return null;
}
